import FileSaver from "file-saver";
import { toast } from "react-toastify";
import qrcode from "qrcode";
import { fullConvertTimestamp } from "./utils";

export const convertSizesUnitToPx = (valueWithUnit) => {
  if (!valueWithUnit) {
    return 0;
  }
  if (typeof valueWithUnit === "number") {
    return valueWithUnit;
  }
  const possibleSizesInPx = {
    cm: 37.8,
    mm: 3.7795275591,
    in: 96,
    px: 1,
    pt: 1.333,
    pc: 16,
  };

  const parts = valueWithUnit.match(/([\d.]+)(\D*)/);
  const value = parts[1];
  const unit = parts[2];

  if (!!unit) {
    if (Object.keys(possibleSizesInPx).includes(unit)) {
      return Math.ceil(possibleSizesInPx[unit] * value);
    }
  }
  if (typeof parseFloat(value) === "number") {
    return Math.ceil(value);
  }
  return NaN;
};

export function removeXmlDeclaration(inputString) {
  // Create a regular expression to match XML declarations
  const xmlDeclarationRegex = /<\?XML[^>]*\?>/gi;

  // Create a regular expression to match XML declarations
  const aboveSVGRegex = /([\s\S]*?)(?=<svg)/i;
  const match = inputString.match(aboveSVGRegex);
  // Use the replace method to remove all XML declarations

  let resultString = inputString;
  if (!!match && Array.isArray(match) && !!match[0]) {
    resultString = inputString.replace(match[0], "");
  }

  return resultString;
}
export function getEverythingAboveSVGStart(inputString) {
  const xmlDeclarationRegex = /<\?XML[^>]*\?>/gi;
  // Create a regular expression to match XML declarations
  const aboveSVGRegex = /([\s\S]*?)(?=<svg)/i;
  const match = inputString.match(aboveSVGRegex);
  if (match) {
    const resultString = match[0].replace(xmlDeclarationRegex, "");

    return resultString.trim();
  }
  return "";
}

export const mergeSVG = ({
  stickerImage,
  stickerWidth,
  stickerHeight,
  qrLeftPos,
  qrTopPos,
  qrHeight,
  qrWidth,
  qrCode,
}) => {
  const aspectRatio1 = stickerWidth / stickerHeight;

  // Create the SVG string
  const svgString = `<?xml version="1.0"  encoding="UTF-8"?>
   ${getEverythingAboveSVGStart(stickerImage)}
    <svg xmlns="http://www.w3.org/2000/svg" width="${stickerWidth}" height="${
    stickerWidth / aspectRatio1
  }">
  <g>
      ${removeXmlDeclaration(stickerImage)}
      <svg x="${qrLeftPos}" y="${qrTopPos}" width="${qrHeight}" height="${qrWidth}">
        ${qrCode}
      </svg>
      </g>
    </svg>
  `;

  return svgString;
};

export const mergeSVGForDownload = ({
  stickerImage,
  stickerWidth,
  stickerHeight,
  qrLeftPos,
  qrTopPos,
  qrHeight,
  qrWidth,
  qrCode,
}) => {
  const aspectRatio1 = stickerWidth / stickerHeight;
  const characterReferenceRegex = /&\w+;/g;

  const svgString = `<?xml version="1.0"  encoding="UTF-8"?>
   ${getEverythingAboveSVGStart(stickerImage)}
    <svg xmlns="http://www.w3.org/2000/svg" width="${stickerWidth}" height="${
    stickerWidth / aspectRatio1
  }">
  <g>
      ${removeXmlDeclaration(stickerImage)
        .replace(characterReferenceRegex, (match) => {
          return `&amp;${match.substring(1, match.length - 1)};`;
        })
        .replace(/:([a-zA-Z-]+)/g, (match, p1) => `:${p1.toLowerCase()}`)}
      <svg x="${qrLeftPos}" y="${qrTopPos}" width="${qrHeight}" height="${qrWidth}">
        ${qrCode}
      </svg>
      </g>
    </svg>
  `;

  return svgString;
};

export const addBorderRadiusToSVG = (svgString, cornerRadius, lightColor) => {
  // Create an SVG element from the string
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(svgString, "image/svg+xml");

  // Find the first <path> element
  const pathElement = xmlDoc.querySelector("path:first-child");

  if (pathElement) {
    // Create a new <rect> element with rounded corners for all corners
    const rectElement = xmlDoc.createElementNS(
      "http://www.w3.org/2000/svg",
      "rect"
    );
    // rectElement.setAttribute("x", "0");
    // rectElement.setAttribute("y", "0");
    rectElement.setAttribute("width", "100%");
    rectElement.setAttribute("height", "100%");

    // Set the same corner radius for all corners
    rectElement.setAttribute("rx", cornerRadius);
    rectElement.setAttribute("ry", cornerRadius);

    rectElement.setAttribute("fill", lightColor); // Preserve the fill property, but make it transparent
    rectElement.setAttribute("stroke", "none"); // Preserve the stroke property, but make it transparent

    // Append the <rect> element before the <path> element
    pathElement.parentNode.insertBefore(rectElement, pathElement);

    // Remove the <path> element
    pathElement.remove();
  }

  // Serialize the modified SVG back to a string
  const modifiedSVGString = new XMLSerializer().serializeToString(xmlDoc);

  return modifiedSVGString;
};

export const TEST_QR_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 41 41" shape-rendering="crispEdges"><path fill="#ffffff" d="M0 0h41v41H0z"/><path stroke="#000000" d="M4 4.5h7m2 0h1m2 0h1m1 0h1m3 0h2m1 0h1m1 0h1m2 0h7M4 5.5h1m5 0h1m2 0h1m5 0h1m2 0h1m2 0h1m2 0h1m1 0h1m5 0h1M4 6.5h1m1 0h3m1 0h1m1 0h1m1 0h1m1 0h1m1 0h1m1 0h1m4 0h1m2 0h1m1 0h1m1 0h3m1 0h1M4 7.5h1m1 0h3m1 0h1m1 0h2m5 0h1m2 0h1m2 0h1m1 0h2m1 0h1m1 0h3m1 0h1M4 8.5h1m1 0h3m1 0h1m1 0h1m5 0h4m2 0h1m1 0h1m3 0h1m1 0h3m1 0h1M4 9.5h1m5 0h1m1 0h2m2 0h2m3 0h1m1 0h1m2 0h1m3 0h1m5 0h1M4 10.5h7m1 0h1m1 0h1m1 0h1m1 0h1m1 0h1m1 0h1m1 0h1m1 0h1m1 0h1m1 0h7M12 11.5h2m1 0h1m4 0h1m2 0h4m1 0h1M4 12.5h1m1 0h5m2 0h3m1 0h2m2 0h1m1 0h2m1 0h3m1 0h5M6 13.5h1m8 0h3m2 0h2m1 0h1m1 0h3m2 0h1m2 0h4M4 14.5h2m2 0h6m1 0h1m1 0h3m1 0h1m4 0h1m1 0h1m3 0h1m1 0h1M4 15.5h2m7 0h1m2 0h2m1 0h4m2 0h1m6 0h3M6 16.5h3m1 0h2m1 0h2m3 0h3m3 0h1m2 0h2m4 0h1m2 0h1M4 17.5h1m4 0h1m1 0h1m2 0h2m3 0h1m2 0h1m2 0h2m3 0h2m3 0h2M4 18.5h1m1 0h1m3 0h1m3 0h1m2 0h1m1 0h2m1 0h1m1 0h1m3 0h8M4 19.5h3m7 0h2m1 0h3m5 0h3m3 0h1m2 0h1M4 20.5h1m2 0h1m2 0h1m3 0h3m2 0h1m1 0h1m2 0h1m1 0h1m1 0h2m1 0h3m2 0h1M4 21.5h1m1 0h1m1 0h1m2 0h3m1 0h2m1 0h1m1 0h1m2 0h1m1 0h3m2 0h2m1 0h4M6 22.5h1m1 0h1m1 0h1m1 0h3m1 0h2m1 0h1m2 0h1m2 0h1m4 0h1m1 0h1m1 0h1M6 23.5h4m4 0h1m1 0h1m1 0h3m4 0h1m2 0h1m1 0h1m1 0h4M5 24.5h1m3 0h2m2 0h1m1 0h1m1 0h1m1 0h1m2 0h1m2 0h1m2 0h1m2 0h3M4 25.5h2m3 0h1m2 0h1m1 0h1m1 0h2m1 0h2m3 0h2m1 0h2m1 0h1m3 0h1m1 0h1M4 26.5h1m3 0h3m3 0h1m2 0h1m3 0h3m1 0h7m2 0h2M4 27.5h1m1 0h2m3 0h1m1 0h1m1 0h1m1 0h1m2 0h1m1 0h4m2 0h7m1 0h1M4 28.5h1m3 0h1m1 0h1m1 0h1m1 0h1m1 0h1m3 0h5m3 0h5m2 0h1M12 29.5h1m1 0h5m2 0h3m2 0h1m1 0h1m3 0h1m1 0h3M4 30.5h7m3 0h1m2 0h1m1 0h1m1 0h1m2 0h1m1 0h3m1 0h1m1 0h1m1 0h1M4 31.5h1m5 0h1m1 0h1m2 0h2m3 0h4m3 0h2m3 0h1m1 0h1m1 0h1M4 32.5h1m1 0h3m1 0h1m1 0h1m3 0h1m2 0h2m3 0h3m1 0h6M4 33.5h1m1 0h3m1 0h1m1 0h2m2 0h2m1 0h1m1 0h2m4 0h1m1 0h1m1 0h4m1 0h1M4 34.5h1m1 0h3m1 0h1m1 0h1m2 0h2m1 0h4m2 0h1m3 0h1m1 0h2m1 0h2M4 35.5h1m5 0h1m2 0h1m4 0h2m5 0h1m2 0h1m1 0h3m1 0h1M4 36.5h7m1 0h2m1 0h3m1 0h4m1 0h1m3 0h1m1 0h4m1 0h1"/></svg>`;

export function SvgToPngExporter(
  svgString,
  w,
  h,
  fileName = "Sticker",
  getURL = false
) {
  return new Promise<void | string>((resolve, reject) => {
    // Scaled 5 times
    const dpi = 500; // Set the desired DPI (e.g., 300 DPI)
    const scale = dpi / 96; // 96 DPI is the default screen DPI

    // Create a temporary canvas with the desired DPI
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      // Set up CSS size.
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";

      // Set the canvas dimensions to match the desired DPI
      canvas.width = w * scale;
      canvas.height = h * scale;

      // Scale the context to match the DPI
      context.scale(scale, scale);

      // Draw the SVG image onto the canvas
      context.drawImage(
        img,
        0,
        0,
        w * scale,
        h * scale,
        0,
        0,
        w * scale,
        h * scale
      );

      // Convert the canvas to a PNG data URL
      const pngDataUrl = canvas.toDataURL("image/png");
      if (getURL) {
        resolve(pngDataUrl);
      } else {
        // Create a temporary link element to trigger the download
        const downloadLink = document.createElement("a");
        downloadLink.href = pngDataUrl;
        downloadLink.download = `${fileName} -${fullConvertTimestamp(
          Date.now()
        )}-exported.png`;
        downloadLink.click();
        resolve();
      }
    };

    img.onerror = (error) => {
      reject(error);
    };

    img.src = `data:image/svg+xml;base64,${btoa(svgString)}`;
  });
}

export async function svgFileToString(imageUrl) {
  try {
    const response = await fetch(imageUrl, { mode: "cors" });
    const text = await response?.text();

    return text;
  } catch (err) {
    console.log("ERROR ON SVG FILE TO STRING", err);
  }
}

export function getImageDataURL(svgXml) {
  return `data:image/svg+xml;base64,${btoa(svgXml)}`;
}

export const downloadFile = async (url: any, fileName?: string) => {
  try {
    if (!fileName) {
      fileName = "Sticker";
    }
    await FileSaver.saveAs(
      url,
      `${fileName} -${fullConvertTimestamp(Date.now())}`
    );
  } catch (error) {
    toast.error("Error downloading the Qr");
  }
};

export const downloadSVG = async (from: "svgString" | "url", data) => {
  if (from === "url") {
    await downloadFile(data);
  }
  if (from === "svgString") {
    const svgData = getImageDataURL(data);
    await downloadFile(svgData);
  }
};

export const loadQRCode = async (stickerDimensions, frontEndURL) => {
  const options = {
    errorCorrectionLevel: "M",
    type: "svg",
    color: {
      light: stickerDimensions?.qrOptions?.color?.light || "#fff",
      dark: stickerDimensions?.qrOptions?.color?.dark || "#000",
    },
    margin: stickerDimensions?.qrOptions?.margin || 1,
    borderRadius: stickerDimensions?.qrOptions?.borderRadius || 0,
  };
  const data = await qrcode.toString(frontEndURL, options);

  const svgString = addBorderRadiusToSVG(
    data,
    String(stickerDimensions?.qrOptions?.borderRadius || 0),
    stickerDimensions?.qrOptions?.color?.light || "#fff"
  );
  return svgString;
};

function calculateOpacity(hexValue) {
  // Convert hex to decimal
  const decimalValue = parseInt(hexValue, 16);

  // Calculate opacity percentage (0 to 100)
  const opacityPercentage = (decimalValue / 255).toFixed(2);

  return opacityPercentage;
}

function addFillOpacity(inputString, value) {
  const regexPattern = /(fill=")/g;
  const replacementString = `fill-opacity="${value}" $1`;
  const outputString = inputString.replace(regexPattern, replacementString);
  return outputString;
}

export const downloadMergedSVG = async (
  sticker,
  frontEndURL,
  getURL = false
) => {
  let qrLightColor =
    sticker?.stickerDimension?.qrOptions?.color?.light || "#ffffff";
  let qrLightColorOpacity = "1";

  if (qrLightColor?.length > 7) {
    const opacityHex = qrLightColor.substring(qrLightColor.length - 2);
    qrLightColorOpacity = calculateOpacity(opacityHex);
    qrLightColor = qrLightColor.slice(0, -2);
  }

  const dimensionForQR = {
    qrOptions: {
      ...sticker?.stickerDimension,
      color: {
        ...sticker?.stickerDimension?.qrOptions?.color,
        light: qrLightColor,
      },
    },
  };

  let qrSVG = await loadQRCode(dimensionForQR, frontEndURL);
  qrSVG = addFillOpacity(qrSVG, qrLightColorOpacity);
  const stickerSVG = await svgFileToString(sticker?.URL);
  if (!stickerSVG) {
    return "";
  }
  const { stickerDimension } = sticker;
  const mergedSVGString = await mergeSVGForDownload({
    stickerImage: stickerSVG,
    stickerWidth: stickerDimension?.stickerWidth,
    stickerHeight: stickerDimension?.stickerHeight,
    qrWidth: stickerDimension?.qrWidth,
    qrHeight: stickerDimension?.qrHeight,
    qrTopPos: stickerDimension?.qrTop,
    qrLeftPos: stickerDimension?.qrLeft,
    qrCode: qrSVG,
  });

  const imageURL = getImageDataURL(mergedSVGString);
  if (getURL) {
    return imageURL;
  }
  downloadFile(imageURL);
};

export const downloadMergedPNG = async (
  sticker,
  frontEndURL,
  getURL = false
) => {
  const qrSVG = await loadQRCode(sticker?.stickerDimension, frontEndURL);
  const stickerSVG = await svgFileToString(sticker?.URL);
  if (!stickerSVG) {
    return "";
  }

  const { stickerDimension } = sticker;
  const mergedSVGString = await mergeSVG({
    stickerImage: stickerSVG,
    stickerWidth: stickerDimension?.stickerWidth,
    stickerHeight: stickerDimension?.stickerHeight,
    qrWidth: stickerDimension?.qrWidth,
    qrHeight: stickerDimension?.qrHeight,
    qrTopPos: stickerDimension?.qrTop,
    qrLeftPos: stickerDimension?.qrLeft,
    qrCode: qrSVG,
  });

  const url = await SvgToPngExporter(
    mergedSVGString,
    stickerDimension?.stickerWidth,
    stickerDimension?.stickerHeight,
    "Sticker",
    getURL
  );

  if (!!url) {
    return url;
  }
};

export const downloadQRSVG = async (sticker, frontEndURL) => {
  const qrSVG = await loadQRCode(sticker?.stickerDimension, frontEndURL);

  const imageURL = getImageDataURL(qrSVG);
  downloadFile(imageURL, "QR");
};

export const downloadQRPNG = async (sticker, frontEndURL) => {
  const { stickerDimension } = sticker;
  const options = {
    errorCorrectionLevel: "M",
    scale: 250,
    color: {
      light: stickerDimension?.qrOptions?.color?.light || "#fff",
      dark: stickerDimension?.qrOptions?.color?.dark || "#000",
    },
    margin: stickerDimension?.qrOptions?.margin || 1,
    borderRadius: stickerDimension?.qrOptions?.borderRadius || 0,
  };
  qrcode
    .toDataURL(frontEndURL, options)
    .then((url) => {
      downloadFile(url, "QR");
    })
    .catch((err) => {
      console.error(err);
    });
};
